import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { MatProgressBar } from '@angular/material/progress-bar'
import { ActivatedRoute, Router } from '@angular/router'
import {
  ApiService,
  AppointmentDataService,
  AppointmentModel,
  Clinic,
  Clinician,
  ClinicService,
  ConfirmService,
  CustomQuestionSchema,
  FileService,
  getIntakeSections,
  getQuestionsBySection,
  Intake,
  IntakeModel,
  IntakeSection,
  IntakeService,
  IQuestion,
  ISection,
  isHiddenSection,
  mashQuestions,
  MatProgressButtonOptions,
  NOT_FOUND,
  Patient,
  PatientDataService,
  PatientIntakeFormData,
  PatientPalRestError,
  ProductModel,
  ProgressIndicatorService,
} from '@mmx/shared'
import { TranslateService } from '@ngx-translate/core'
import { find, isEqual, map, sortBy } from 'lodash'
import { forkJoin, Subject } from 'rxjs'
import { filter, takeUntil } from 'rxjs/operators'

@Component({
  selector: 'mmx-patient-intake',
  templateUrl: './patient-intake.component.html',
  styleUrls: ['./patient-intake.component.scss'],
  standalone: false,
})
export class PatientIntakeComponent implements OnInit, OnDestroy {
  @ViewChild(MatProgressBar) progressBar: MatProgressBar
  @Input() embedded = false

  appointment: AppointmentModel
  products: ProductModel[]
  intake: IntakeModel
  intakeData = new PatientIntakeFormData()
  patient: Patient
  clinic: Clinic
  clinicians: Clinician[]
  documentTypes: string[] = []
  sections: IntakeSection[] = []
  customSections: ISection[] = []
  questions: IQuestion[] = []
  questionsByLeg: ISection[] = []
  isLoading = false

  btnSignOff: MatProgressButtonOptions = {
    active: false,
    text: 'patient-intake.sign-intake',
    buttonColor: 'primary',
    barColor: 'primary',
    matType: 'mat-raised-button',
    mode: 'indeterminate',
    value: 0,
    disabled: false,
    buttonIcon: {
      fontIcon: 'save',
    },
  }

  private onDestroy$: Subject<void> = new Subject<void>()

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private restService: ApiService,
    private fileService: FileService,
    private progressService: ProgressIndicatorService,
    private patientDataService: PatientDataService,
    private appointmentDataService: AppointmentDataService,
    private clinicService: ClinicService,
    private confirmService: ConfirmService,
    private intakeService: IntakeService,
    private translateService: TranslateService,
  ) {}

  get clinicianName(): string | void {
    if (this.intakeService.clinicians && this.intake.isConfirmed) {
      const clinician = this.intakeService.clinicians.find(
        (c) => c.id === this.intake.confirmed?.clinicianId,
      )
      if (clinician) {
        return clinician.name
      }
    }
  }

  get unsignerName(): string | void {
    if (this.intakeService.clinicians && this.intake.unsigned != null) {
      const clinician = this.intakeService.clinicians.find(
        (c) => c.id === this.intake.unsigned?.clinicianId,
      )
      if (clinician) {
        return clinician.name
      }
    }
  }

  get imageQuestions(): CustomQuestionSchema[] {
    return ((this.intake && this.intake.customQuestions) || []).filter(
      (q) => q.value && q.isImage,
    )
  }

  ngOnInit() {
    this.intakeService.onIntakeChange
      .pipe(
        takeUntil(this.onDestroy$),
        filter(Boolean),
      )
      .subscribe((intake) => {
        this.intake = intake
        this.intakeData.fromJson(intake)

        // Refresh sections
        this.sections = map(this.sections, (section) => {
          section.isHidden = isHiddenSection(
            section.requirements,
            this.intake,
          )
          return section
        })
      })

    this.isLoading = true
    this.clinic = this.clinicService.clinic
    this.documentTypes = this.clinic.settings.customDocumentTypes ?? []

    if (!this.clinic.modules.tech) {
      this.btnSignOff.text = 'patient-intake.finalize-intake'
    }

    this.patientDataService.patient$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((patient) => (this.patient = patient))

    this.appointmentDataService.appointment$
      .pipe(
        takeUntil(this.onDestroy$),
        filter(appointment => appointment.intakeId != null),
      )
      .subscribe((appointment) => {
        this.appointment = appointment
        this.products = appointment.products

        const questionParams = {
          includeHidden: true,
          appointment: this.appointment.id,
          intake: this.appointment.intakeId,
          sex: this.patient != null ? this.patient.sex : undefined,
        }

        forkJoin([
          this.intakeService.getIntakeSections({
            appointment: this.appointment.id,
            intake: this.appointment.intakeId,
            includeHidden: 'Y',
          }),
          this.intakeService.getQuestions(questionParams),
          this.clinicService.getClinicians(),
          this.intakeService.load(this.appointment.intakeId),
        ])
          .pipe(takeUntil(this.onDestroy$))
          .subscribe({
            next: ([customSections, questions, clinicians]) => {
              this.questionsByLeg = questions.data
              this.questions = mashQuestions(this.questionsByLeg)
              this.customSections = customSections.data
              this.intakeService.clinicians = clinicians
              this.getSections()
              this.progressBar.mode = 'determinate'
              this.isLoading = false
            },
            error: (err: PatientPalRestError) => {
              if (err.status === NOT_FOUND) {
                this.route.parent.params.subscribe((p) => {
                  this.router.navigate([
                    `/patient/${p.patientId}/appointment/${this.appointment.id}`,
                  ])
                })
              } else {
                throw err
              }
            },
          })
      })
  }

  ngOnDestroy(): void {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  getLegQuestions(label: string) {
    return getQuestionsBySection(
      label,
      this.appointment,
      this.intakeData,
      this.questionsByLeg,
    )
  }

  getQuestionField(id: string, field: string) {
    const question = find(this.questions, (q) => q.id === id)
    return question ? question[field] : ''
  }

  save() {
    this.progressBar.mode = 'indeterminate'

    const message =
      !this.clinic.modules.tech && !this.intake.isSignedOff
        ? 'patient-intake.confirm-finalize'
        : 'patient-intake.confirm-sign'
    this.confirmService
      .confirm({
        message,
        noBtnLabel: 'common.cancel',
        yesBtnLabel: 'common.yes',
      })
      .subscribe(async (isConfirmed) => {
        if (isConfirmed) {
          const process = this.progressService.start()
          await this.intakeService.onSaveAll.next(true)
          this.intakeData.stage = Intake.STAGE.TECH_CONFIRMED

          this.restService.updateIntake(this.intakeData).subscribe(
            (intake) => {
              this.intake = intake
              this.intakeData.fromJson(intake)
              this.intakeService.onIntakeChange.next(intake)
              this.progressBar.mode = 'determinate'
              this.resetProgressButton()

              if (this.clinic.modules.tech) {
                process.stop(
                  this.translateService.instant(
                    'patient-intake.intake-signed-off',
                  ),
                )
              } else {
                process.stop(
                  this.translateService.instant(
                    'patient-intake.intake-finalized',
                  ),
                )
              }
            },
            () => {
              this.progressBar.mode = 'determinate'
              this.resetProgressButton()
              process.stop(
                this.translateService.instant(
                  'patient-intake.error-updating-intake',
                ),
              )
            },
          )
        } else {
          this.resetProgressButton()
          this.progressBar.mode = 'determinate'
        }
      })
  }

  markConfirmed() {
    this.btnSignOff.active = true
    this.save()
  }

  download(event: MouseEvent, file?: string) {
    const asHtml = event.altKey
    this.progressBar.mode = 'indeterminate'
    this.restService
      .getIntakePdfLink(this.intake.id, {
        file,
        html: asHtml,
      })
      .subscribe(
        (resp) => {
          this.fileService.saveAs(resp.data)
          this.progressBar.mode = 'determinate'
        },
        () => {
          this.progressBar.mode = 'determinate'
        },
      )
  }

  downloadImage(image: CustomQuestionSchema) {
    this.fileService.saveAs({
      url: image.value,
      pdfFileName: `image.${image.extension}`,
    })
  }

  private getSections() {
    const sections = sortBy(
      getIntakeSections(
        this.clinic,
        this.appointment,
        this.intakeService.data,
        this.customSections,
        this.questionsByLeg,
      ),
      'order',
    )

    if (isEqual(this.sections, sections)) {
      /**
       * If the sections haven't changed, don't update to avoid unnecessary re-rendering
       * This re-rendering closes any open mat-expansion-panels which causes a bad UX when filling out the form
       * The underlying reason is apps/embedded/src/app/statuses/statuses.component.ts:73-78
       * Where appointment is reloaded every 15 seconds, which causes the intake to be reloaded
       */
      return
    }

    this.sections = sections
    this.intakeService.sections = this.sections
  }

  private resetProgressButton() {
    this.btnSignOff.active = false
  }
}
